import { initializeApp } from "firebase/app";
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  inMemoryPersistence,
  connectAuthEmulator,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
} from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getAnalytics } from "firebase/analytics";
import {
  getRemoteConfig as getFirebaseRemoteConfig,
  isSupported as remoteConfigIsSupported,
  type RemoteConfig,
} from "firebase/remote-config";
import { currentConfig } from "./appConfig";

const config = currentConfig();

export function isProduction(): boolean {
  return config.isNativePlatform === true
    ? !navigator.userAgent.includes("com.uniqueminds.staging")
    : import.meta.env.VITE_FIREBASE_ENV === "prod" ||
        config.productionDomains.includes(window.location.hostname);
}

export function isEmulator(): boolean {
  return (
    import.meta.env.VITE_USE_EMULATORS === "true" && !config.isNativePlatform
  );
}

export const firebaseConfig = isProduction()
  ? config.production
  : config.staging;

console.log(
  `Initializing Firebase with ${
    isProduction() ? "PRODUCTION" : "STAGING"
  } configuration`
);

// Initialize Firebase Core
export const app = initializeApp(firebaseConfig);
export const auth = config.offlinePlayback
  ? initializeAuth(app, { persistence: inMemoryPersistence })
  : config.isNativePlatform
  ? initializeAuth(app, {
      persistence: browserLocalPersistence,
    })
  : getAuth(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-west1");

let analyticsInstance;
try {
  // Web gtag must never run inside the native webview: it registers device
  // users against the WEB app registration, double-counting them as phantom
  // web-stream users. Native analytics goes through nativeAnalytics instead.
  analyticsInstance =
    firebaseConfig.measurementId && !config.isNativePlatform && !config.offlinePlayback
      ? getAnalytics(app)
      : undefined;
} catch (error) {
  console.warn("Firebase Analytics failed to initialize:", error);
}
export const analytics = analyticsInstance;

// Web Remote Config instance. Skipped on native when a nativeRemoteConfig
// implementation is provided — the native SDK presents the device app identity
// (required for Firebase A/B); the web SDK would present the web app identity.
export let remoteConfig: RemoteConfig | undefined;
export const remoteConfigInitialization = (async () => {
  if (
    config.offlinePlayback ||
    !config.remoteConfig ||
    (config.isNativePlatform && config.nativeRemoteConfig)
  )
    return;
  try {
    // Checking only for window.indexedDB is insufficient in restricted Safari:
    // the API can exist while opening a database throws SecurityError.
    if (await remoteConfigIsSupported())
      remoteConfig = getFirebaseRemoteConfig(app, {});
    else
      console.warn(
        "Remote Config storage unavailable; using bundled defaults."
      );
  } catch (error) {
    console.warn("Remote Config unavailable; using bundled defaults.", error);
  }
})();

// Connect to emulators
if (isEmulator() && !config.offlinePlayback) {
  // Browser devices on the same LAN must reach the development host, not
  // their own loopback interface. Native apps never enter emulator mode.
  const host = window.location.hostname;
  connectFunctionsEmulator(functions, host, 5001);
  connectAuthEmulator(auth, `http://${host}:9099`);
  connectFirestoreEmulator(db, host, 8080);
  connectStorageEmulator(storage, host, 9199);
}

// Auth providers
export const googleProvider = new GoogleAuthProvider();
export const appleProvider = new OAuthProvider("apple.com");
